import { useState } from "react"
import type { Folder } from "shared/src/DatabaseObjects/DatabaseObjects"
import { useLoadContext } from "shared/src/libs/contextLib/contextLib"
import { useMountEffect } from "shared/src/libs/hooksLib/hooksLib"
import { updateFolder } from "shared/src/libs/apiLib/apiLib"
import { TwoWayMap } from "shared/src/libs/objLib/objLib"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shared/src/components/shadcn/components/ui/card"
import { Field, FieldLabel, FieldContent } from "shared/src/components/shadcn/components/ui/field"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "shared/src/components/shadcn/components/ui/select"

const EXPIRATION_OPTIONS = {
	2: "2 days",
	7: "1 week",
	14: "2 weeks",
	21: "3 weeks",
	28: "1 month",
	90: "3 months",
	180: "6 months",
	365: "1 year",
	730: "2 years",
} as const

const expirationMapping = new TwoWayMap(EXPIRATION_OPTIONS)

// Helper to convert display string to days number
function getExpirationDays(displayValue: string): number {
	const key = expirationMapping.backward(displayValue)
	return Number(key)
}

// Helper to convert days number to display string
function getExpirationDisplay(days: number | string): string {
	const numDays = typeof days === "string" ? parseInt(days) : days
	const result = expirationMapping.forward(numDays)
	return typeof result === "string" ? result : ""
}

export function DataTab() {
	const { getFolderForId, company, setFolders } = useLoadContext()
	const [folder, setFolder] = useState<Folder | null>(null)
	const [currentSelection, setCurrentSelection] = useState<string>("")

	useMountEffect(() => {
		const folderIdToFetch = `${company.id}_presentingcv`
		getFolderForId({ id: folderIdToFetch, forceDownload: true }).then((folder) => {
			const typedFolder = folder as Folder

			if (typedFolder.app_expiration_days) {
				const displayValue = getExpirationDisplay(typedFolder.app_expiration_days)
				setFolder(typedFolder)
				setCurrentSelection(displayValue)
			}
		})
	})

	function setNewExpiration(newSelection: string) {
		const folderId = folder?.folder_id
		const clientId = folderId?.slice(0, -13)
		const newExpiration = getExpirationDays(newSelection)

		const body = {
			clientId,
			folderId,
			updateType: "expiration",
			newExpiration,
		}
		setCurrentSelection(newSelection)

		// Update local state and cache immediately
		if (folder && folderId) {
			const updatedFolder = {
				...folder,
				app_expiration_days: newExpiration,
			}
			setFolder(updatedFolder)
			void setFolders({ from: "singleItem", for: clientId ?? "", itemId: folderId, item: updatedFolder })
		}

		updateFolder({ body })
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Data Retention</CardTitle>
					<CardDescription>Configure how long application data is stored before automatic deletion.</CardDescription>
				</CardHeader>
				<CardContent>
					<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
						<FieldLabel htmlFor="delete-after">Auto delete data after</FieldLabel>
						<FieldContent className="max-w-md">
							<Select value={currentSelection} onValueChange={setNewExpiration}>
								<SelectTrigger id="delete-after" className="w-[200px]">
									<SelectValue placeholder="Select duration" />
								</SelectTrigger>
								<SelectContent>
									{Array.from(expirationMapping.values).map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FieldContent>
					</Field>
				</CardContent>
			</Card>
		</div>
	)
}
